import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar2 from "../Components/Navbar2";
import { FiArrowUpRight, FiX } from "react-icons/fi";
import VoicesInteriorScene from "../voices/VoicesInteriorScene";
import "../Components/css/Careers.css";
import "../voices/voices-public-pages.css";

const jobs = [
  {
    id: "ai-agentic-workflow-builder",
    jobCode: "#ANZ-AI-2047",
    title: "AI-Agentic Workflow Builder",
    type: "Full time",
    country: "India",
    package: "10-20 LPA",
    summary:
      "Build practical agentic workflows that reduce manual work, connect business systems, and make teams faster without adding complexity.",
    description:
      "You will work with consultants and engineers to map process gaps, design AI-assisted workflows, configure automations, test outputs, and document clean handoffs for client teams.",
    responsibilities: [
      "Translate business processes into clear workflow maps and automation stories.",
      "Configure AI-assisted task flows, prompts, checks, and human approval points.",
      "Test agent behavior against real-world edge cases and document outcomes.",
      "Partner with Salesforce, integration, and data teams to move workflows into production.",
    ],
    profile: [
      "Strong structured thinking and comfort with process documentation.",
      "Hands-on exposure to AI tools, CRM workflows, or automation platforms.",
      "Clear written communication and willingness to test details repeatedly.",
    ],
  },
  {
    id: "agentic-enterprise-engineer",
    jobCode: "#ANZ-ENG-9183",
    title: "Agentic Enterprise Engineer",
    type: "Full time",
    country: "India",
    package: "10-20 LPA",
    summary:
      "Engineer enterprise-grade agentic systems across CRM, data, integration, and automation platforms with security, observability, and scale in mind.",
    description:
      "You will design and implement backend services, integration contracts, orchestration patterns, and governance controls that let AI workflows operate reliably in business environments.",
    responsibilities: [
      "Design secure agent orchestration patterns for enterprise systems.",
      "Build APIs, integration flows, event-driven automations, and operational guardrails.",
      "Create monitoring, audit, fallback, and approval patterns for production workflows.",
      "Work directly with delivery teams to convert client needs into deployable architecture.",
    ],
    profile: [
      "Strong engineering foundation in APIs, databases, cloud services, or Salesforce.",
      "Experience with automation, integration, or AI-assisted engineering patterns.",
      "Comfort owning production-quality code and reviewing technical tradeoffs.",
    ],
  },
  {
    id: "associate-engineer",
    jobCode: "#ANZ-AE-1208",
    title: "Associate Engineer",
    type: "Full time",
    country: "India",
    package: "8-12 LPA",
    summary:
      "Support enterprise engineering delivery across Salesforce, integrations, automation, QA checks, and implementation documentation.",
    description:
      "You will work with senior engineers and consultants to convert requirements into configured features, clean technical notes, test cases, and production-ready handoffs.",
    responsibilities: [
      "Assist with Salesforce configuration, integration checks, automation testing, and delivery documentation.",
      "Write clear requirement notes, test cases, defect summaries, and deployment handoff material.",
      "Support QA, data validation, and release-readiness checks across client workstreams.",
      "Learn Antrazal's enterprise engineering, Salesforce, and AI workflow delivery standards.",
    ],
    profile: [
      "Strong engineering fundamentals and willingness to work across configuration, data, and documentation.",
      "Comfort with APIs, databases, Salesforce, automation tools, or structured QA work.",
      "High ownership, fast learning, and careful attention to production details.",
    ],
  },
  {
    id: "agentic-sales-evangelist",
    jobCode: "#ANZ-SALES-7304",
    title: "Agentic Sales Evangelist",
    type: "Full time",
    country: "US",
    package: "90-140K USD",
    summary:
      "Help customers understand where agentic workflows create measurable business value and shape opportunities from first conversation to solution fit.",
    description:
      "You will lead discovery, translate business pain into agentic workflow opportunities, create concise solution narratives, and work with delivery teams on demos and proposals.",
    responsibilities: [
      "Run discovery conversations around AI automation, CRM, and operating model gaps.",
      "Build clear narratives, demos, and proposals for agentic workflow solutions.",
      "Partner with delivery leaders to qualify scope, risk, and measurable outcomes.",
      "Represent Antrazal's point of view through client conversations and market content.",
    ],
    profile: [
      "Consultative sales or solutions experience in SaaS, CRM, AI, or automation.",
      "Ability to explain technical ideas in direct business language.",
      "Strong ownership of pipeline quality, follow-through, and client context.",
    ],
  },
];

const initialApplicationState = {
  name: "",
  email: "",
  phone: "",
  city: "",
  linkedin: "",
  portfolio: "",
  currentRole: "",
  message: "",
  captchaAnswer: "",
  website: "",
  consent: false,
};

const apiBaseUrl = () => (process.env.REACT_APP_PRODUCTION_API_URL || "").replace(/\/$/, "");

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState(initialApplicationState);
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState({ loading: false, message: "", error: "" });
  const [captcha, setCaptcha] = useState({ loading: false, question: "", token: "", imageUrl: "" });

  const currentJob = useMemo(
    () => jobs.find((job) => job.id === selectedJob?.id) || selectedJob,
    [selectedJob]
  );

  useEffect(() => {
    document.body.style.overflow = currentJob ? "hidden" : "";
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeApplication();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [currentJob]);

  const loadCaptcha = async () => {
    setCaptcha({ loading: true, question: "", token: "", imageUrl: "" });
    try {
      const response = await fetch(
        `${apiBaseUrl()}/backend/api/v1.0/AntrazalSolutionsPrivatelimited/contactus/captcha`
      );
      if (!response.ok) {
        throw new Error("Captcha request failed.");
      }
      const data = await response.json();
      setCaptcha({
        loading: false,
        question: data.question || "Enter the verification code shown in the image.",
        token: data.token || "",
        imageUrl: data.imageUrl || "",
      });
    } catch {
      setCaptcha({ loading: false, question: "", token: "", imageUrl: "" });
      setSubmitState((previous) => ({
        ...previous,
        error: "Captcha could not be loaded. Please reopen the form and try again.",
      }));
    }
  };

  const openApplication = (job) => {
    setSelectedJob(job);
    setFormData(initialApplicationState);
    setErrors({});
    setSubmitState({ loading: false, message: "", error: "" });
    loadCaptcha();
  };

  const closeApplication = () => {
    setSelectedJob(null);
    setErrors({});
    setSubmitState({ loading: false, message: "", error: "" });
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setSubmitState((previous) => ({ ...previous, error: "", message: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9\s-]{7,20}$/;

    if (!formData.name.trim()) {
      nextErrors.name = "Full name is required.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.phone.trim()) {
      nextErrors.phone = "Phone number is required.";
    } else if (!phonePattern.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number.";
    }
    if (!formData.city.trim()) {
      nextErrors.city = "City is required.";
    }
    if (!formData.currentRole.trim()) {
      nextErrors.currentRole = "Current role or background is required.";
    }
    if (!formData.portfolio.trim()) {
      nextErrors.portfolio = "Resume/profile link is required.";
    } else if (!/^https?:\/\/[^\s]+\.[^\s]+$/i.test(formData.portfolio.trim())) {
      nextErrors.portfolio = "Enter a public resume/profile link starting with http or https.";
    }
    if (!captcha.token) {
      nextErrors.captchaAnswer = "Verification code is not ready. Please refresh it.";
    } else if (!formData.captchaAnswer.trim()) {
      nextErrors.captchaAnswer = "Verification code is required.";
    }
    if (!formData.consent) {
      nextErrors.consent = "Consent is required before applying.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitApplication = async (event) => {
    event.preventDefault();
    if (!currentJob || !validate()) {
      return;
    }

    setSubmitState({ loading: true, message: "", error: "" });

    const content = [
      `Career application for: ${currentJob.title}`,
      `Job ID: ${currentJob.jobCode}`,
      `Package: ${currentJob.package}`,
      `Current role/background: ${formData.currentRole}`,
      `LinkedIn: ${formData.linkedin || "Not provided"}`,
      `Resume/profile link: ${formData.portfolio}`,
      "",
      formData.message || "No additional message provided.",
    ].join("\n");

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      company: "Career applicant",
      jobTitle: currentJob.title,
      country: currentJob.country || "India",
      city: formData.city.trim(),
      content,
      createdBy: formData.name.trim(),
      updatedBy: formData.name.trim(),
      emailsChecked: false,
      privacyChecked: true,
      captchaToken: captcha.token,
      captchaAnswer: formData.captchaAnswer.trim(),
      website: formData.website,
    };

    try {
      const response = await fetch(
        `${apiBaseUrl()}/backend/api/v1.0/AntrazalSolutionsPrivatelimited/contactus`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.message || "Application could not be submitted right now.");
      }

      setSubmitState({
        loading: false,
        message: "Application submitted. Our team will review the profile.",
        error: "",
      });
      setFormData(initialApplicationState);
      loadCaptcha();
    } catch (error) {
      loadCaptcha();
      setSubmitState({
        loading: false,
        message: "",
        error: error.message || "Application could not be submitted. Please check the fields and try again.",
      });
    }
  };

  return (
    <>
      <Navbar2 />

      <main className="careers-page vpp-page vpp-careers" data-voices-theme="dark">
        <VoicesInteriorScene as="section" scene="stack" className="vpp-careers-hero" aria-label="Careers at Antrazal" data-voices-theme="dark">
          <div className="container careers-hero__inner">
            <nav className="careers-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">Careers</span></nav>
            <p className="careers-eyebrow">Careers at Antrazal</p>
            <h1>Good work.<br /><em>Great people.</em></h1>
            <p className="careers-hero__lead">
              Antrazal hires people who can turn ambiguous business problems into clean operating
              systems across Salesforce, automation, data, and agentic workflows.
            </p>
            <div className="careers-hero__actions">
              <a className="careers-hero__button" href="#role-profiles">
                Explore role profiles <FiArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </VoicesInteriorScene>

        <section className="careers-section careers-jobs" id="role-profiles" data-voices-theme="dark">
          <div className="container">
            <div className="careers-section__header careers-reference-intro">
              <div><p className="sectionLabel">Small teams. Clear ownership.</p><h2>Serious delivery.<br /><em>Room to grow.</em></h2></div>
              <p>The work is hands-on, practical, and close to the business outcome. Explore our role profiles.</p>
            </div>
            <p className="careers-availability">Confirm current role availability and compensation with the hiring team.</p>
            <div className="careers-job-list">
              {jobs.map((job) => (
                <article className="careers-job-card" key={job.id}>
                  <div className="careers-job-card__main">
                    <div className="careers-job-card__meta">
                      <span>{job.jobCode} / {job.type}</span>
                    </div>
                    <h3>{job.title}</h3>
                    <p>{job.summary}</p>
                  </div>
                  <p className="careers-job-location">{job.country}<br />{job.package}</p>
                  <div className="careers-job-card__details">
                    <button type="button" onClick={() => openApplication(job)}>
                      Apply now <FiArrowUpRight aria-hidden="true" />
                    </button>
                  </div>
                  <details className="careers-role-details">
                    <summary>Role focus and profile</summary>
                    <p>{job.description}</p>
                    <div>
                      <section><h4>Role focus</h4><ul>{job.responsibilities.map(item => <li key={item}>{item}</li>)}</ul></section>
                      <section><h4>Profile</h4><ul>{job.profile.map(item => <li key={item}>{item}</li>)}</ul></section>
                    </div>
                  </details>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="careers-section careers-how" aria-labelledby="careers-how-title" data-voices-theme="dark">
          <div className="container careers-how-grid">
            <div><p className="sectionLabel">How we work</p><h2 id="careers-how-title">Close to the client.<br /><em>Close to the craft.</em></h2></div>
            <div className="careers-how-list">
              {['Direct client context, clean execution, and the discipline of shipping useful systems.', 'Enterprise systems across CRM, data, and operations.', 'Agentic workflows with human controls.', 'Small teams with direct ownership.'].map((text, index) => <div key={text}><small>0{index + 1}</small><p>{text}</p></div>)}
            </div>
          </div>
        </section>
      </main>

      {currentJob && (
        <div className="careers-modal vpp-application" role="dialog" aria-modal="true" aria-labelledby="careers-modal-title" data-voices-theme="dark">
          <div className="careers-modal__backdrop" onClick={closeApplication} />
          <div className="careers-modal__panel">
            <button className="careers-modal__close" type="button" onClick={closeApplication} aria-label="Close">
              <FiX aria-hidden="true" />
            </button>
            <div className="careers-modal__header">
              <p className="careers-eyebrow">Apply for</p>
              <h2 id="careers-modal-title">{currentJob.title}</h2>
              <div className="careers-modal__meta" aria-label="Role details">
                {[currentJob.jobCode, currentJob.type, currentJob.country, currentJob.package].filter(Boolean).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
              <p>
                Complete the profile below. The application will be saved and emailed to the
                recruitment team for review.
              </p>
            </div>

            <div className="careers-role-profile">
              <div>
                <h3>Role focus</h3>
                <ul>
                  {currentJob.responsibilities.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>Profile</h3>
                <ul>
                  {currentJob.profile.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <form className="careers-application-form" onSubmit={submitApplication}>
              <div className="careers-form-field careers-form-field--full">
                <label htmlFor="career-job">Job profile</label>
                <input id="career-job" type="text" value={`${currentJob.jobCode} · ${currentJob.title}`} readOnly />
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-name">Full name</label>
                <input id="career-name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
                {errors.name && <span>{errors.name}</span>}
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-email">Email</label>
                <input
                  id="career-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="name@example.com"
                />
                {errors.email && <span>{errors.email}</span>}
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-phone">Phone</label>
                <input id="career-phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91..." />
                {errors.phone && <span>{errors.phone}</span>}
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-city">City</label>
                <input id="career-city" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" />
                {errors.city && <span>{errors.city}</span>}
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-linkedin">LinkedIn profile</label>
                <input
                  id="career-linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div className="careers-form-field">
                <label htmlFor="career-portfolio">Resume/profile link</label>
                <input
                  id="career-portfolio"
                  name="portfolio"
                  required
                  value={formData.portfolio}
                  onChange={handleInputChange}
                  placeholder="Drive, LinkedIn, website, or resume URL"
                />
                {errors.portfolio && <span>{errors.portfolio}</span>}
              </div>
              <div className="careers-form-field careers-form-field--full">
                <label htmlFor="career-role">Current role / background</label>
                <input
                  id="career-role"
                  name="currentRole"
                  value={formData.currentRole}
                  onChange={handleInputChange}
                  placeholder="Current role, experience, or strongest skill"
                />
                {errors.currentRole && <span>{errors.currentRole}</span>}
              </div>
              <div className="careers-form-field careers-form-field--full">
                <label htmlFor="career-message">Short note</label>
                <textarea
                  id="career-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us why this role fits your profile."
                />
              </div>
              <div className="careers-form-field careers-form-field--trap" aria-hidden="true">
                <label htmlFor="career-website">Website</label>
                <input
                  id="career-website"
                  name="website"
                  tabIndex="-1"
                  value={formData.website}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
              </div>
              <div className="careers-form-field careers-form-field--captcha">
                <label htmlFor="career-captcha">Verification code</label>
                <div className="careers-captcha">
                  <div className="careers-captcha__challenge" aria-label={captcha.question || "Application verification code"}>
                    {captcha.imageUrl ? (
                      <img src={captcha.imageUrl} alt="Application verification code" />
                    ) : (
                      <span className="careers-captcha__placeholder">
                        {captcha.loading ? "Loading..." : "Refresh required"}
                      </span>
                    )}
                    <button type="button" className="careers-captcha__refresh" onClick={loadCaptcha} disabled={captcha.loading}>
                      Refresh
                    </button>
                  </div>
                  <input
                    id="career-captcha"
                    name="captchaAnswer"
                    value={formData.captchaAnswer}
                    onChange={handleInputChange}
                    placeholder="Type code"
                    inputMode="text"
                    autoComplete="off"
                  />
                </div>
                {errors.captchaAnswer && <span>{errors.captchaAnswer}</span>}
              </div>
              <label className="careers-consent">
                <input name="consent" type="checkbox" checked={formData.consent} onChange={handleInputChange} />
                <span>
                  I consent to Antrazal processing this application information for recruitment review.
                </span>
              </label>
              {errors.consent && <p className="careers-form-error">{errors.consent}</p>}
              {submitState.error && <p className="careers-form-error" role="alert">{submitState.error}</p>}
              {submitState.message && <p className="careers-form-success" role="status">{submitState.message}</p>}
              <div className="careers-form-actions">
                <button type="button" onClick={closeApplication}>
                  Cancel
                </button>
                <button type="submit" disabled={submitState.loading}>
                  {submitState.loading ? "Submitting..." : "Submit application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
